import { FormikProps } from 'formik';
import { t } from 'i18n';
import { useTenantConfig } from 'hooks';
import { useServices } from 'contexts';
import { useProfile } from '@teko/ui-user';
import { ProfileUpdate } from '@teko/user';
import { useUploadProfile } from '../hooks/useUploadProfile';
import { useAuth } from '@teko/auth-react';
import { ButtonInterface, SubTitle, Title, Shape } from '@teko/ds-mobile';
import React, { useEffect, useState, useMemo } from 'react';
import { UpdateProfileFormProps } from './types';
import { fallbackTransform, getProfileInputFields } from './helpers';
import { GeneralFormikForm } from './GeneralFormikForm';
import { OtpConfirmationPopup } from './OtpConfirmationPopup';
import { PrivacyPolicy } from '@teko/privacy-policy';
import { logger } from '@teko/log';
import { SubmittedPrivacyPolicy } from '@teko/privacy-policy';
import { PolicyTypeId } from 'types';
import { PrivacyPolicies } from 'components/PrivacyPolicies';
import { isServerless } from 'helpers';
import { AdditionalButtons } from './AdditionalButtons';

const PrivacyPolicyHeader = 'Chính sách quyền riêng tư';

export const UpdateProfileForm = (props: UpdateProfileFormProps) => {
  const {
    isMobile = false,
    customRequireFields = {},
    transforms,
    additionalButtons,
  } = props;
  const {
    notEditCustomerType,
    partner,
    supportedPhone,
    displayPrivacyPoliciesInProfileForm,
  } = useTenantConfig();
  const { authService, userService, privacyPolicyService } = useServices();
  const { setUser, user: profile, updateProfile } = useAuth();
  const { customerTypes } = useProfile({
    userService,
  });

  const [valuesForOTP, setValuesForOTP] = useState<Partial<ProfileUpdate>>({});

  const [policies, setPolicies] = useState<Array<PrivacyPolicy>>([]);
  const [originalPolicies, setOriginalPolicies] = useState<
    Array<PrivacyPolicy>
  >([]);

  const defaultUploadProfile = async (profile: ProfileUpdate) => {
    return await updateProfile?.(profile);
  };
  const defaultVerifyPhone = async (telephone: string, OTP: string) => {
    await authService.updatePhone(telephone, OTP);
    profile && setUser?.({ ...profile, telephone: telephone });
  };

  const transformedUpdateFunc = fallbackTransform(
    transforms?.uploadProfile,
    defaultUploadProfile,
  );
  const transformedVerifyPhoneFunc = fallbackTransform(
    transforms?.verifyPhone,
    defaultVerifyPhone,
  );

  const {
    waitingVerifyOTP,
    loading,
    otpError,
    otpData,
    requiredFields,
    allowEdit,
    message,
    feedbacks,
    countdown,
    resetStates,
    resendOTP,
    validate,
    update,
    verifyOtp,
  } = useUploadProfile({
    customRequireFields,
    uploadProfile: transformedUpdateFunc,
    verifyPhone: transformedVerifyPhoneFunc,
  });

  useEffect(() => {
    (async () => {
      try {
        const privacyPolicies = await privacyPolicyService.getPolicies([
          'all_policies',
        ]);
        setPolicies(privacyPolicies);
        setOriginalPolicies(privacyPolicies);
      } catch (error) {
        logger.error(error);
      }
    })();
  }, []);

  const submit = async (values: Partial<ProfileUpdate>) => {
    const { name, email, dob, sex, customerTypeId } = values;
    update({ name, email, dob, sex, customerTypeId });
    // submit privacy policy
    try {
      const submitPolicies: SubmittedPrivacyPolicy[] = policies
        .filter((policy) => policy.policyTypeId !== PolicyTypeId.TYPE_1)
        .map(
          ({ id, version, language, isApproved }) =>
            ({
              id,
              version,
              language,
              isApproved: isApproved || false,
            } as SubmittedPrivacyPolicy),
        );
      await privacyPolicyService.submitPolicies(submitPolicies);
      setOriginalPolicies(policies);
    } catch (error) {
      logger.error(error);
    }
    return undefined;
  };

  const comparePolicies = useMemo(() => {
    if (originalPolicies.length !== policies.length) return false;
    for (let i = 0; i < policies.length; i++) {
      if (
        policies[i].isApproved !==
        originalPolicies.find((p) => p.id === policies[i].id)?.isApproved
      )
        return false;
    }
    return true;
  }, [originalPolicies, policies]);

  const getInputFields = (
    values: Partial<ProfileUpdate>,
    setFieldValue: FormikProps<Partial<ProfileUpdate>>['setFieldValue'],
  ) =>
    getProfileInputFields(
      values,
      setFieldValue,
      profile,
      partner,
      customerTypes,
      notEditCustomerType,
      allowEdit,
    );

  const getButtonsProps = (
    values: Partial<ProfileUpdate>,
    _errors: FormikProps<Partial<ProfileUpdate>>['errors'],
    dirty?: boolean,
  ): ButtonInterface[] => [
    !!values.telephone && values.telephone !== profile?.telephone
      ? {
          type: 'button',
          width: isMobile ? '100%' : '',
          onClick: () => {
            const payload = { telephone: values.telephone };
            update(payload);
            setValuesForOTP(payload);
          },
          variant: 'primary',
          disabled: !!feedbacks.telephone || loading || waitingVerifyOTP,
          children: t('Xác nhận số điện thoại'),
        }
      : {
          type: 'submit',
          width: isMobile ? '100%' : '',
          variant: 'primary',
          disabled: (comparePolicies && !dirty) || loading,
          children: t('Cập nhật'),
        },
  ];

  const initialValues: ProfileUpdate = {
    name: profile?.name,
    telephone: profile?.telephone,
    email: profile?.email,
    sex: profile?.sex,
    dob: profile?.dob,
    customerTypeId: profile?.customerTypeId || undefined,
  };

  const renderPolicies =
    policies.length > 0 ? (
      <>
        {isMobile ? (
          <Shape
            style={{
              backgroundColor: '#F6F6F6',
              marginLeft: '-32px',
              marginRight: '-32px',
              padding: '16px 0 6px 0',
            }}
          >
            <SubTitle
              style={{ paddingLeft: 16 }}
              textTransform="uppercase"
              color="textSecondary"
            >
              {t(PrivacyPolicyHeader)}
            </SubTitle>
          </Shape>
        ) : (
          <Shape>
            <Title paddingTop="s40" paddingBottom="s16">
              {t(PrivacyPolicyHeader)}
            </Title>
          </Shape>
        )}
        <Shape paddingTop="s16">
          <PrivacyPolicies
            policies={policies}
            setPolicies={setPolicies}
            trackingScreenName="profile"
            hideWarning={true}
            disableChangeApproval={true}
            isLoggedIn={isServerless() && authService.isLoggedIn()}
            originalPolicies={originalPolicies}
          />
        </Shape>
      </>
    ) : null;

  const renderAdditionalButtons =
    !!additionalButtons && additionalButtons.length > 0 ? (
      <AdditionalButtons additionalButtons={additionalButtons} />
    ) : null;

  return (
    <>
      {!allowEdit && !!supportedPhone && (
        <SubTitle
          color="textSecondary"
          style={{
            fontStyle: 'italic',
          }}
          marginBottom="s16"
        >
          {t(
            'Để thay đổi các thông tin đã cung cấp, vui lòng liên hệ bộ phận CSKH của chúng tôi tại {{userPhone}}',
            {
              userPhone: supportedPhone,
            },
          )}
        </SubTitle>
      )}
      <GeneralFormikForm
        isMobile={isMobile}
        submit={submit}
        validate={validate}
        feedbacks={feedbacks}
        messages={[message]}
        initialFormValues={initialValues}
        requiredFields={requiredFields}
        getInputFields={getInputFields}
        getButtonsProps={getButtonsProps}
        policies={displayPrivacyPoliciesInProfileForm ? renderPolicies : null}
        additionalButtons={renderAdditionalButtons}
      />

      <OtpConfirmationPopup
        isMobile={isMobile}
        values={valuesForOTP}
        waitingVerifyOTP={waitingVerifyOTP}
        otpError={otpError}
        otpData={otpData}
        countdown={countdown}
        resetStates={resetStates}
        resendOTP={resendOTP}
        verifyOtp={verifyOtp}
      />
    </>
  );
};
